'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  DataMessageTypeEnum,
  TerraUIMessageDataPartName,
  type CopilotMode,
  type CopilotModeSwitchMessage,
  type TerraUIMessage,
} from '@terra/shared-dto-chat-types';
import { useChatInstancesStore } from '../store/chatInstancesStore';

function extractModeSwitchMessages(
  messages: TerraUIMessage[],
): Array<{ partId: string; data: CopilotModeSwitchMessage }> {
  const results: Array<{ partId: string; data: CopilotModeSwitchMessage }> = [];

  for (const message of messages) {
    if (message.role !== 'assistant' || !message.parts) {
      continue;
    }

    for (const part of message.parts) {
      if (
        part.type === TerraUIMessageDataPartName &&
        part.data?.type === DataMessageTypeEnum.CopilotModeSwitch
      ) {
        const data = part.data as CopilotModeSwitchMessage;

        results.push({ partId: `${message.id}:${data.id}`, data });
      }
    }
  }

  return results;
}

function isLatestAssistantPart({
  partId,
  messages,
}: {
  partId: string;
  messages: TerraUIMessage[];
}): boolean {
  const lastAssistant = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant');

  return lastAssistant ? partId.startsWith(`${lastAssistant.id}:`) : false;
}

export function resolveRestoredCopilotMode(
  switchMessages: Array<{ data: CopilotModeSwitchMessage }>,
): CopilotMode | undefined {
  return switchMessages.reduce<CopilotMode | undefined>((mode, { data }) => {
    if (!data.requiresConfirmation) {
      return data.toMode;
    }

    return mode ?? data.fromMode;
  }, undefined);
}

export function useRevertCopilotModeSwitch(
  chatId: string,
): (data: { toMode: CopilotMode }) => void {
  const setChatMode = useChatInstancesStore((state) => state.setChatMode);

  return useCallback(
    ({ toMode }: { toMode: CopilotMode }) => {
      setChatMode(chatId, toMode);
    },
    [chatId, setChatMode],
  );
}

export function useCopilotModeSwitch({
  chatId,
  messages,
  isReplayingHistory,
}: {
  chatId: string;
  messages: TerraUIMessage[];
  isReplayingHistory: boolean;
}): void {
  const setChatMode = useChatInstancesStore((state) => state.setChatMode);
  const getChatMode = useChatInstancesStore((state) => state.getChatMode);
  const processedPartIds = useRef<Set<string>>(new Set());
  const restoredChatId = useRef<string | null>(null);

  useEffect(() => {
    const switchMessages = extractModeSwitchMessages(messages);

    if (isReplayingHistory) {
      for (const { partId } of switchMessages) {
        processedPartIds.current.add(partId);
      }

      return;
    }

    if (restoredChatId.current !== chatId) {
      restoredChatId.current = chatId;
      processedPartIds.current = new Set(
        switchMessages.map((item) => item.partId),
      );

      const explicitMode = useChatInstancesStore
        .getState()
        .configs.get(chatId)?.mode;
      const restoredMode = resolveRestoredCopilotMode(switchMessages);

      if (explicitMode === undefined && restoredMode) {
        setChatMode(chatId, restoredMode);
      }

      return;
    }

    for (const { partId, data } of switchMessages) {
      if (processedPartIds.current.has(partId)) {
        continue;
      }

      processedPartIds.current.add(partId);

      if (!isLatestAssistantPart({ partId, messages })) {
        continue;
      }

      const toMode = data.toMode;

      if (getChatMode(chatId) === toMode || data.requiresConfirmation) {
        continue;
      }

      setChatMode(chatId, toMode);
    }
  }, [chatId, messages, isReplayingHistory, getChatMode, setChatMode]);
}
